import classNames from "classnames";
import { html } from "lit-html";
import { MenuItemId, MenuItemWithDropdownContent } from "./header";
import { useState } from "Shared/haunted/CustomHooks";
import { useReduxState } from "Shared/redux/useReduxState";
import { useBenefits } from "./useBenefits";
import { useMobileBenefits } from "./useMobileBenefits";
import { useMobileMyBooking } from "./useMobileMyBooking";
import { useMobileOffersAndDestinations } from "./useMobileOffersAndDestinations";
import { useMobileTravelInformation } from "./useMobileTravelInformation";
import { useMyBooking } from "./useMyBooking";
import { useOffersAndDestinations } from "./useOffersAndDestinations";
import { useTravelInformation } from "./useTravelInformation";

export interface Props {
	culture: string;
	isCollapsed: boolean;
}

export const useHeaderDropdown = (props: Props) => {
	const [kontent] = useReduxState("kontent");

	const [activeItemId, setActiveItemId] = useState<MenuItemId>(undefined);

	const offersAndDestinations = useOffersAndDestinations({ culture: props.culture });
	const mobileOffersAndDestinations = useMobileOffersAndDestinations();

	const myBooking = useMyBooking();
	const mobileMyBooking = useMobileMyBooking();

	const benefits = useBenefits();
	const mobileBenefits = useMobileBenefits();

	const travelInformation = useTravelInformation();
	const mobileTravelInformation = useMobileTravelInformation();

	const dropdownMenus: MenuItemWithDropdownContent[] = [
		{
			id: "offers-and-destinations",
			title: kontent?.dealsAndDestinations.dealsAndDestinationTest.contents.headerTitle.value,
			content: offersAndDestinations.htmlTemplate,
			mobileContent: mobileOffersAndDestinations.htmlTemplate,
		},
		{
			id: "my-booking",
			title: kontent?.myBooking.myBookingTest.contents.headerTitle.value,
			content: myBooking.htmlTemplate,
			mobileContent: mobileMyBooking.htmlTemplate,
		},
		{
			id: "benefits",
			title: kontent?.smartBenefits.smartBenefitsTest.contents.headerTitle.value,
			content: benefits.htmlTemplate,
			mobileContent: mobileBenefits.htmlTemplate,
		},
		{
			id: "travel-information",
			title: kontent?.travelInformation.travelInformationPersonas.contents.headerTitle.value,
			content: travelInformation.htmlTemplate,
			mobileContent: mobileTravelInformation.htmlTemplate,
		},
	];

	const contentTemplate = () =>
		dropdownMenus.map(
			(item) => html`
				<div
					class=${classNames(
						"absolute inset-0 w-full h-full transition-all duration-250 ease-in-out flex origin-top-center justify-center",
						{
							"opacity-100 scale-100 z-10 pointer-events-auto": activeItemId === item.id,
							"opacity-0 scale-75 -z-10 pointer-events-none": activeItemId !== item.id,
						}
					)}
				>
					${item.content()}
				</div>
			`
		);

	const htmlTemplate = () => html`
		<div
			class=${classNames(
				"relative hidden z-[99999] w-full h-[500px] justify-center bg-white shadow-xl transform duration-250 ease-in-out transition-[opacity,max-height] origin-top-center overflow-hidden",
				"md:flex",
				{
					"opacity-0 pointer-events-none max-h-0": !activeItemId || props.isCollapsed,
					"opacity-100": activeItemId && !props.isCollapsed,
				}
			)}
		>
			${contentTemplate()}
		</div>
	`;

	return {
		activeId: activeItemId,
		dropdownMenus,
		closeMenu: () => setActiveItemId(undefined),
		htmlTemplate,
		openMenu: setActiveItemId,
		toggleMenuItem: (id: MenuItemId) => setActiveItemId(activeItemId === id ? undefined : id),
	};
};
